import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import NoMessageAlert from '@/components/atom/NoMessageAlert/NoMessageAlert';
import ChatInput from '@/components/molecule/ChatInput/ChatInput';
import FeedbackNudge from '@/components/molecule/FeedbackNudge/FeedbackNudge';
import FollowUpChipList from '@/components/molecule/FollowUpChipList/FollowUpChipList';
import ProcessingStatuses from '@/components/molecule/ProcessingStatuses/ProcessingStatuses';
import WelcomeSection from '@/components/molecule/WelcomeSection/WelcomeSection';
import ExampleQuestionsList from '@/components/organism/ExampleQuestionsList/ExampleQuestionsList';
import SafetyNotice from '@/components/organism/SafetyNotice/SafetyNotice';
import SpeechBubble from '@/components/organism/SpeechBubble/SpeechBubble';
import { ChatWindowWrapper, ContentWrapper, MessagesSection } from '@/components/pages/ChatWindow/ChatWindow.styles';
import { getUserProfile, updateUserProfile } from '@/configuration/login/loginSlice';
import { FOLLOW_UP_CHIPS_ACTIONS, FOLLOW_UP_CHIPS_CATEGORY, gaPush } from '@/configuration/setup/googleAnalytics';
import { useAppDispatch, useAppSelector } from '@/configuration/setup/hooks';
import { fetchUserPreferences } from '@/services/chatApi/fetchUserPreferences';
import { selectFollowUps, selectIsFetchingHistory, selectMessagesByActiveThread } from '@/store/chat/chat.slice';
import { setCurrentLayout } from '@/store/layout/layoutSlice';
import { selectIsOnboardingQuestionLoading, Steps } from '@/store/onboarding/onboardingSlice';
import { addStatus, clearStatuses } from '@/store/processingStatus/processingStatusSlice';
import {
  selectActiveThreadId,
  selectIsActiveThreadRestorePending,
  selectIsThreadLoading,
} from '@/store/thread/threadSlice';
import { isPastDate } from '@/utils/dateHelpers';
import { getUserId } from '@/utils/getUserId';

import { THandleSendParams } from '../ChatPage/types';
import { useFeedbackNudge } from './useFeedbackNudge';

const knowledgeBaseSteps = {
  1: {
    id: 'onb-step-1-knowledge-base',
    message: 'chatRouting.processing.determiningService',
    completed: false,
    visible: true,
    threadId: 'onboarding-thread',
    type: undefined,
  },
  2: {
    id: 'onb-step-2-knowledge-base',
    message: 'knowledgeBase.processing.searchingDocuments',
    completed: false,
    visible: true,
    threadId: 'onboarding-thread',
    type: undefined,
  },
  3: {
    id: 'onb-step-3-knowledge-base',
    message: 'knowledgeBase.processing.documents',
    completed: false,
    visible: true,
    threadId: 'onboarding-thread',
    type: undefined,
  },
  4: {
    id: 'onb-step-4-knowledge-base',
    message: 'knowledgeBase.processing.finalAnswer',
    completed: false,
    visible: true,
    threadId: 'onboarding-thread',
    type: undefined,
  },
};

const performSteps = {
  1: {
    id: 'onb-step-1-perform',
    message: 'chatRouting.processing.determiningService',
    completed: false,
    visible: true,
    threadId: 'onboarding-thread',
    type: undefined,
  },
  2: {
    id: 'onb-step-2-perform',
    message: 'performService.processing.performData',
    completed: false,
    visible: true,
    threadId: 'onboarding-thread',
    type: undefined,
  },
  3: {
    id: 'onb-step-3-perform',
    message: 'performService.processing.finalAnswer',
    completed: false,
    visible: true,
    threadId: 'onboarding-thread',
    type: undefined,
  },
};

type ChatWindowProps = {
  handleSend: (params: THandleSendParams) => Promise<void>;
  handleDelete: (messageId: string) => void;
  isLoading?: boolean;
  isError?: boolean;
};

const ChatWindow = ({ handleSend, handleDelete, isError, isLoading }: ChatWindowProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const onboardingTimeouts = useRef<NodeJS.Timeout[]>([]);
  const activeThreadId = useAppSelector(selectActiveThreadId);
  const isRestoringActiveThread = useAppSelector(selectIsActiveThreadRestorePending);
  const isOnboardingQuestionLoading = useAppSelector(selectIsOnboardingQuestionLoading);
  const messages = useAppSelector(selectMessagesByActiveThread);

  const wrapperRef = useRef<HTMLDivElement>(null);

  const isThreadsLoading = useAppSelector(selectIsThreadLoading(activeThreadId ?? null));
  const isHistoryFetching = useAppSelector(selectIsFetchingHistory);

  const user = useAppSelector(getUserProfile);
  const userId = getUserId(user?.sub ?? '');

  const [showSafetyNotice, setShowSafetyNotice] = useState(false);
  const [messageCount, setMessageCount] = useState(messages?.length);

  const { showNudge, handleNudgeAction, handleNudgeClose } = useFeedbackNudge(messages);

  if (messageCount !== messages?.length) setMessageCount(messages?.length);

  useEffect(() => {
    if (!userId) return;

    const loadUserPreferences = async () => {
      try {
        const data = await fetchUserPreferences(userId);
        if (!data) return;

        const shouldShowSafetyNotice = isPastDate(data.safety_notice_hidden_until);
        if (!shouldShowSafetyNotice) {
          dispatch(updateUserProfile({ fleetName: data.fleet_name }));
        }
        setShowSafetyNotice(shouldShowSafetyNotice);
      } catch (err) {
        console.error('Error fetching processing status:', err);
      }
    };

    loadUserPreferences();
  }, [userId]);

  const hasActiveDialog = () => Boolean(document.querySelector('[role="dialog"]'));

  const handleSendFromInput = async (message: string) => {
    await handleSend({ content: message, isOnboarding: false });
  };

  const scrollToBottom = () => {
    const shouldScroll = !hasActiveDialog() && !showNudge && wrapperRef.current;

    if (shouldScroll) {
      wrapperRef.current.scrollTo({
        top: wrapperRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  };

  const handleSetShow = (value: boolean) => setShowSafetyNotice(value);

  const handleCloseSafetyNotice = () => {
    setShowSafetyNotice(false);
    dispatch(setCurrentLayout('about'));
    navigate('/about', { replace: true });
  };

  useEffect(() => {
    if (messages) scrollToBottom();
  }, [messageCount, isLoading]);

  const handleOnboardingSteps = (step: Steps) => {
    const steps = step === 'step-4' ? knowledgeBaseSteps : performSteps;
    const stepKeys = Object.keys(steps);

    stepKeys.forEach((key, index) => {
      const timeout = setTimeout(() => {
        dispatch(addStatus((steps as Record<string, (typeof steps)[keyof typeof steps]>)[key]));
      }, index * 6000);
      onboardingTimeouts.current.push(timeout);
      dispatch(clearStatuses());
    });
  };

  useEffect(() => {
    if (isOnboardingQuestionLoading) {
      onboardingTimeouts.current.forEach(clearTimeout);
      onboardingTimeouts.current = [];
      handleOnboardingSteps('step-4');
    }
  }, [isOnboardingQuestionLoading]);

  // INFO: While the persisted thread is being restored the app does not yet know whether the chat is
  // empty, so all three states stay hidden and nothing is rendered until the lifecycle is terminal.
  const shouldShowEmptyState =
    (!Array.isArray(messages) || activeThreadId === null) &&
    !isThreadsLoading &&
    !isHistoryFetching &&
    !isError &&
    !isRestoringActiveThread;

  const shouldShowNoMessages =
    Array.isArray(messages) &&
    messages.length === 0 &&
    !isThreadsLoading &&
    !isHistoryFetching &&
    !isRestoringActiveThread &&
    activeThreadId !== null;

  const showSpinner = Array.isArray(messages) && messages.length === 0 && isHistoryFetching && !isRestoringActiveThread;

  const lastAiMessageIndex = Array.isArray(messages) ? messages.findLastIndex((m) => m.role === 'ai') : -1;
  const followUps = useAppSelector(selectFollowUps);
  const lastMessageIsAi = lastAiMessageIndex === (messages?.length ?? 0) - 1;
  const showFollowUps = lastMessageIsAi && (followUps?.length ?? 0) > 0;

  const handleChipClick = useCallback(
    (text: string) => {
      gaPush({ category: FOLLOW_UP_CHIPS_CATEGORY, action: FOLLOW_UP_CHIPS_ACTIONS.CLICK_FOLLOW_UP, label: text });
      handleSend({ content: text, isOnboarding: false });
    },
    [handleSend],
  );

  return (
    <ChatWindowWrapper>
      {showSafetyNotice ? (
        <SafetyNotice show={showSafetyNotice} setShow={handleSetShow} onClose={handleCloseSafetyNotice} />
      ) : null}
      <ContentWrapper ref={wrapperRef}>
        {showSpinner ? <Spinner data-testid="spinner" /> : null}
        {shouldShowNoMessages ? <NoMessageAlert /> : null}
        {shouldShowEmptyState ? <WelcomeSection /> : null}
        <MessagesSection>
          {Array.isArray(messages) &&
            messages.map((msg, index) => (
              <SpeechBubble
                key={`speechBubble-${index}`}
                messageId={msg.messageId}
                handleSend={handleSend}
                handleDelete={handleDelete}
                isLoading={isLoading}
                data-testid="speechbubble"
              />
            ))}
          {showFollowUps ? (
            <FollowUpChipList followUps={followUps!} onChipClick={handleChipClick} disabled={isLoading} />
          ) : null}
          {(isThreadsLoading && isLoading) || isOnboardingQuestionLoading ? <ProcessingStatuses /> : null}
        </MessagesSection>
      </ContentWrapper>
      {showNudge ? <FeedbackNudge onAction={handleNudgeAction} onClose={handleNudgeClose} /> : null}
      <ChatInput
        isLoading={isThreadsLoading}
        onSendMessage={(message) => handleSendFromInput(message)}
        bottomLeftSlot={
          <ExampleQuestionsList
            handleSend={handleSend}
            isLoading={isLoading}
            shouldShowEmptyState={shouldShowEmptyState}
          />
        }
      />
    </ChatWindowWrapper>
  );
};

export default ChatWindow;
